// ============================================================
// A CONSULTA DA LISTAGEM DE PROFISSIONAIS (Story 31.1, Epic 31)
//
// O contrato entre a tela e `GET /api/professionals`: que filtros, que ordenação,
// que colunas. Vive aqui porque a tela ESCREVE a consulta (e, a partir da 31.2,
// guarda-a no URL e numa vista salva) e a API LÊ-A: uma só função de cada lado é
// o que impede os dois de discordarem sobre o que um parâmetro quer dizer.
//
// O FORMATO NO URL É PLANO, e não um JSON num parâmetro: `?sort=-situation&
// situation=available,leaving&state=SP`. Lê-se num link, e cada filtro novo é um
// parâmetro novo, validado aqui.
//
// DOIS REGIMES DE VALIDAÇÃO, de propósito:
//   • `page`, `pageSize`, `archived` e `q` são TOLERANTES, como sempre foram:
//     lixo cai no default, e só a string exata `true` revela os arquivados
//     (fail-closed). Mudar isto partiria links e chamadores que já existem.
//   • a ordenação, as colunas e os filtros são ESTRITOS: uma chave que não existe
//     é 400. Uma ordenação ignorada em silêncio é uma tabela que mente sobre a
//     ordem; uma coluna desconhecida aceite seria a porta para pedir o que a
//     lista não pode mostrar.
// ============================================================
import { z } from 'zod'

import {
  PROFESSIONAL_SENIORITIES,
  WORK_MODELS,
  type ProfessionalSeniority,
  type WorkModel,
} from './dtos.ts'
import { BOND_STATUSES, type BondStatus } from './genesis-bonds.ts'

/**
 * Onde a pessoa está em relação ao trabalho, DERIVADO na leitura (não há coluna).
 * A ordem é a de ocupação, do mais ocupado ao mais livre — é a ordem do «Situação ↑».
 *
 *   • `allocated`  — tem alocação EM VIGOR que não acaba nos próximos 30 dias;
 *   • `leaving`    — está em vigor, mas tudo o que tem acaba em até 30 dias;
 *   • `starting`   — tem alocação VIVA que ainda não começou (`started_on > hoje`).
 *                    Já está comprometida: nunca conta como disponível;
 *   • `in_process` — sem alocação viva, com candidatura ativa;
 *   • `available`  — nada disso.
 *
 * A regra vive em SQL (`apps/api/src/lib/professional-situation.ts`), sobre os
 * helpers de «viva» e «em vigor»; este ficheiro só dá os nomes.
 */
export const PROFESSIONAL_SITUATIONS = [
  'allocated',
  'leaving',
  'starting',
  'in_process',
  'available',
] as const
export type ProfessionalSituation = (typeof PROFESSIONAL_SITUATIONS)[number]

/** A janela do «A sair»: término em até tantos dias a contar de hoje. */
export const LEAVING_WINDOW_DAYS = 30

/**
 * A cadência do 1:1 (Story 31.4, decisão do owner no #467): quem está em vigor
 * numa alocação e não tem 1:1 há MAIS do que isto, em dias de calendário de São
 * Paulo, tem o «1:1 devido». Um 1:1 no dia D desliga o aviso até ao dia D + 30,
 * inclusive; no D + 31 volta a ligar.
 */
export const ONE_ON_ONE_CADENCE_DAYS = 30

/**
 * O filtro pergunta «aceita CLT?» ou «aceita PJ?». O `both` que a pessoa declara
 * responde que sim às duas — é o servidor que o inclui, e por isso não é um valor
 * do filtro.
 */
export const CONTRACT_FILTERS = ['clt', 'pj'] as const
export type ContractFilter = (typeof CONTRACT_FILTERS)[number]

/**
 * As colunas de EXIBIÇÃO que vêm sempre na resposta — as de baixa sensibilidade que
 * a lista já devolvia. A resposta traz ainda campos que não são colunas (`id`,
 * `notes`, `phoneNormalized`, `archivedAt` e o `photoUpdatedAt` da 31.8); a projeção exata vive em
 * `professionalsListQuery`. ⚠️ Os três salários e `isCurrentlyEmployed` não estão
 * em lado nenhum dela nem vão estar: a listagem é uma leitura em massa NÃO
 * auditada (SEC-PII-LIST-001), e eles vivem em `GET /:id`, que regista quem os leu.
 */
export const PROFESSIONAL_LIST_BASE_COLUMNS = [
  'name',
  'email',
  'phone',
  'linkedinUrl',
  'githubUsername',
  'city',
  'state',
  'country',
  'openToRemote',
  'openToHybrid',
  'openToOnsite',
  'currentRole',
  'currentCompany',
  'noticePeriodDays',
  'willingToRelocate',
  'preferredContractType',
  'applicationCount',
  // Story 31.5: a senioridade é uma coluna do perfil, e vem na lista como as outras.
  'seniority',
  'createdAt',
  'updatedAt',
] as const

/**
 * As colunas DERIVADAS: custam uma subconsulta por linha, e só se calculam quando pedidas.
 *
 * As três do contato (Story 31.4) leem o caderno com o predicado de visibilidade
 * de quem PEDE: um apontamento `private` de outra pessoa não move a data que eu
 * vejo, e um meu move.
 *   • `lastContact`  — o último apontamento da GENESIS sobre a pessoa (instante);
 *   • `lastOneOnOne` — o último 1:1 (instante);
 *   • `oneOnOneDue`  — em vigor numa alocação e sem 1:1 há mais de
 *                      `ONE_ON_ONE_CADENCE_DAYS` (ou nunca).
 */
export const PROFESSIONAL_LIST_DERIVED_COLUMNS = [
  'situation',
  'currentAllocation',
  'activeApplicationCount',
  'lastContact',
  'lastOneOnOne',
  'oneOnOneDue',
  // Story 31.6: a função principal («Desenvolvimento › Backend») e as três skills de
  // nível mais alto. Lidas do catálogo da 31.5.
  'primaryRole',
  'topSkills',
  // Story 31.8: TODAS as skills da pessoa, com o id — a matriz escolhe as colunas
  // depois de saber quais são as da fatia (`/api/professionals/skill-coverage`).
  'skillLevels',
  // Story 38.4: o estado do vínculo com a Genesis (Candidato, Alocado, Sem posição,
  // Ex-colaborador). Não se ordena por ela, como pelas skills.
  'bondStatus',
  // Story 38.6: aprovado em vaga — alguma candidatura «Aprovada» ou «Contratada».
  'approved',
] as const
export type ProfessionalListDerivedColumn = (typeof PROFESSIONAL_LIST_DERIVED_COLUMNS)[number]

/**
 * Os FORMATOS da lista (Story 31.8), sobre a mesma consulta e os mesmos filtros. São
 * da tela, e não da API — mas uma vista guarda-o, e é o servidor quem valida o que
 * uma vista guarda (`validateViewParams`). A tabela é a de sempre e não se escreve.
 */
export const PROFESSIONAL_LIST_FORMATS = ['table', 'cards', 'matrix'] as const
export type ProfessionalListFormat = (typeof PROFESSIONAL_LIST_FORMATS)[number]

/**
 * Quantas skills a matriz mostra SEM filtro de skill: as mais frequentes da fatia.
 * Com filtro, são as do filtro (no máximo `SKILL_FILTER_MAX`).
 */
export const SKILL_COVERAGE_TOP = 8

/**
 * A COBERTURA de uma skill na fatia inteira (todas as páginas): quantas pessoas a
 * têm, e quantas com prova de entrevista que sustenta o nível delas — a mesma regra
 * do ✓ da lista. É o rodapé da matriz, e a escolha das colunas dela.
 */
export interface SkillCoverage {
  skillId: string
  name: string
  people: number
  evidenced: number
}

export interface SkillCoverageResponse {
  skills: SkillCoverage[]
}

/**
 * Por onde a lista se AGRUPA (Story 31.10). O servidor ordena primeiro pela chave do
 * grupo — cada grupo é um bloco contíguo da lista, e não membros espalhados pelas
 * páginas — e devolve o total de cada grupo na lista INTEIRA.
 *
 *   • `situation`       — pela ordem da situação (`PROFESSIONAL_SITUATIONS`);
 *   • `currentCustomer` — pelo cliente da alocação atual, de A a Z, sem cliente no fim.
 */
export const PROFESSIONAL_LIST_GROUPS = ['situation', 'currentCustomer'] as const
export type ProfessionalListGroup = (typeof PROFESSIONAL_LIST_GROUPS)[number]

/**
 * O total de um grupo na lista inteira. `key` é a chave com que a resposta marca as
 * linhas (a situação, ou o cliente NORMALIZADO — a mesma expressão da ordenação, para
 * «Itaú» e «itau» serem um grupo só); `label` é o nome a mostrar (o cliente como foi
 * escrito; `null` na situação, que a tela traduz). `key` `null` é «sem cliente atual».
 */
export interface ProfessionalListGroupTotal {
  key: string | null
  label: string | null
  total: number
}

/** A lista branca inteira: o que a tela pode pedir em `columns`. */
export const PROFESSIONAL_LIST_COLUMNS = [
  ...PROFESSIONAL_LIST_BASE_COLUMNS,
  ...PROFESSIONAL_LIST_DERIVED_COLUMNS,
] as const
export type ProfessionalListColumn = (typeof PROFESSIONAL_LIST_COLUMNS)[number]

/** Por onde se pode ordenar. Ordenar por uma derivada não obriga a pedi-la em `columns`. */
export const PROFESSIONAL_LIST_SORT_KEYS = [
  'name',
  'createdAt',
  'updatedAt',
  'applicationCount',
  'activeApplicationCount',
  'situation',
  'currentCustomer',
  'state',
  'city',
  'noticePeriodDays',
  'lastContact',
  'lastOneOnOne',
] as const
export type ProfessionalListSortKey = (typeof PROFESSIONAL_LIST_SORT_KEYS)[number]

export const PROFESSIONAL_LIST_PAGE_SIZE_DEFAULT = 50
export const PROFESSIONAL_LIST_PAGE_SIZE_MAX = 200
/** As 27 unidades federativas: mais valores que isto no filtro de UF não é um filtro. */
const STATE_FILTER_MAX = 27

/**
 * Os tetos dos filtros da Story 31.6. Os ids vêm do pedido e são PARÂMETROS da
 * consulta — e a lista vive no teto de 100 do D1 (o teste dos tetos, na API, mede o
 * pior caso com todos no máximo). O nível mínimo e a senioridade vão como literais.
 */
export const AREA_FILTER_MAX = 5
export const ROLE_FILTER_MAX = 10
export const SKILL_FILTER_MAX = 5

/** Um id do catálogo: curto e sem nada que não seja letra, dígito, hífen ou sublinhado. */
const CATALOG_ID = /^[A-Za-z0-9_-]{1,64}$/

/** «Tem todas» (E) ou «tem alguma» (OU) das skills pedidas. */
export const SKILL_FILTER_MODES = ['all', 'any'] as const
export type SkillFilterMode = (typeof SKILL_FILTER_MODES)[number]

/** Uma skill pedida: o id do catálogo e o nível mínimo (1 Básico … 4 Referência). */
export interface SkillFilter {
  skillId: string
  minLevel: number
}

export interface ProfessionalListFilters {
  situation?: ProfessionalSituation[]
  /** Story 38.4 — o estado do vínculo com a Genesis (`BOND_STATUSES`). */
  bond?: BondStatus[]
  /**
   * Story 38.6 — «Aprovado em vaga»: alguma candidatura «Aprovada» ou «Contratada»
   * (`APPROVED_APPLICATION_STATUSES`), arquivada ou não. Só `true`, como o `evidenced`.
   * «No banco» é ele com a situação Em processo ou Disponível (sem alocação viva).
   */
  approved?: true
  /** UF normalizada (maiúsculas, aparada). Comparada com `upper(trim(state))`. */
  state?: string[]
  workModel?: WorkModel[]
  relocate?: boolean
  contract?: ContractFilter[]
  noticeMaxDays?: number
  /** Story 31.6 — a ÁREA inclui as especialidades dela; a função é QUALQUER uma da pessoa. */
  area?: string[]
  role?: string[]
  skills?: SkillFilter[]
  /** Só existe com `skills`. Por omissão, `all`. */
  skillMode?: SkillFilterMode
  seniority?: ProfessionalSeniority[]
  /**
   * Story 31.7 — «só evidenciadas»: com skills pedidas, cada uma tem de ter PROVA de
   * uma entrevista cuja nota SUSTENTA o nível mínimo pedido
   * (`SKILL_LEVEL_SCORE_FLOOR`); sem skills, a pessoa tem de ter alguma skill com prova.
   */
  evidenced?: true
}

export interface ProfessionalListSort {
  key: ProfessionalListSortKey
  dir: 'asc' | 'desc'
}

export interface ProfessionalListQuery {
  page: number
  pageSize: number
  archived: boolean
  q: string
  sort: ProfessionalListSort
  columns: ProfessionalListColumn[]
  filters: ProfessionalListFilters
  /** Story 31.10. Ausente, a lista não agrupa. */
  group?: ProfessionalListGroup
}

export const PROFESSIONAL_LIST_DEFAULT_SORT: ProfessionalListSort = {
  key: 'createdAt',
  dir: 'desc',
}

/** `a,b, a` → `['a', 'b']`: aparado, sem vazios e sem repetidos. Ausente é `[]`. */
function csv(raw: string | undefined): string[] {
  if (raw === undefined) return []
  return [
    ...new Set(
      raw
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean),
    ),
  ]
}

/** Inteiro tolerante — o comportamento de sempre de `page`/`pageSize`. */
function lenientInt(raw: string | undefined, fallback: number): number {
  return Number.parseInt(raw ?? '', 10) || fallback
}

const csvOf = <T extends readonly [string, ...string[]]>(values: T, max: number) =>
  z
    .string()
    .optional()
    .transform(csv)
    .pipe(z.array(z.enum(values)).max(max))

const RawSchema = z.object({
  page: z.string().optional(),
  pageSize: z.string().optional(),
  archived: z.string().optional(),
  q: z.string().optional(),
  sort: z
    .string()
    .optional()
    .refine(
      (s) =>
        s === undefined ||
        (PROFESSIONAL_LIST_SORT_KEYS as readonly string[]).includes(s.replace(/^-/, '')),
      { message: 'Ordenação desconhecida.' },
    ),
  columns: csvOf(PROFESSIONAL_LIST_COLUMNS, PROFESSIONAL_LIST_COLUMNS.length),
  situation: csvOf(PROFESSIONAL_SITUATIONS, PROFESSIONAL_SITUATIONS.length),
  bond: csvOf(BOND_STATUSES, BOND_STATUSES.length),
  // Só `true`: «nunca aprovado» não é uma pergunta que a lista faça.
  approved: z.literal('true').optional(),
  state: z
    .string()
    .optional()
    .transform((s) => [...new Set(csv(s).map((x) => x.toUpperCase()))])
    .pipe(z.array(z.string().max(100)).max(STATE_FILTER_MAX)),
  workModel: csvOf(WORK_MODELS, WORK_MODELS.length),
  relocate: z.enum(['true', 'false']).optional(),
  contract: csvOf(CONTRACT_FILTERS, CONTRACT_FILTERS.length),
  noticeMaxDays: z
    .string()
    .regex(/^\d+$/, 'Inteiro de 0 a 365.')
    .transform(Number)
    .pipe(z.number().int().min(0).max(365))
    .optional(),
  area: z
    .string()
    .optional()
    .transform(csv)
    .pipe(z.array(z.string().regex(CATALOG_ID)).max(AREA_FILTER_MAX)),
  role: z
    .string()
    .optional()
    .transform(csv)
    .pipe(z.array(z.string().regex(CATALOG_ID)).max(ROLE_FILTER_MAX)),
  // `id:nível`, separados por vírgula. Uma skill repetida é recusada — com dois
  // níveis mínimos, qual valeria?
  skill: z
    .string()
    .optional()
    .transform(csv)
    .pipe(
      z
        .array(z.string().regex(/^[A-Za-z0-9_-]{1,64}:[1-4]$/, 'Use id:nível, com nível de 1 a 4.'))
        .max(SKILL_FILTER_MAX)
        .refine((xs) => new Set(xs.map((x) => x.split(':')[0])).size === xs.length, {
          message: 'A mesma skill aparece duas vezes.',
        }),
    ),
  skillMode: z.enum(SKILL_FILTER_MODES).optional(),
  seniority: csvOf(PROFESSIONAL_SENIORITIES, PROFESSIONAL_SENIORITIES.length),
  // Só `true`: «não evidenciadas» não é uma pergunta que a lista faça.
  evidenced: z.literal('true').optional(),
  // Estrito, como a ordenação: um agrupamento ignorado em silêncio é uma lista que
  // diz estar agrupada e não está.
  group: z.enum(PROFESSIONAL_LIST_GROUPS).optional(),
})

export type ProfessionalListQueryParseResult =
  | { success: true; data: ProfessionalListQuery }
  | { success: false; error: z.ZodError }

/**
 * Lê a consulta dos parâmetros do URL (`c.req.query()` na API,
 * `Object.fromEntries(searchParams)` na tela). Parâmetros que a lista não conhece
 * são ignorados, como sempre foram.
 */
export function parseProfessionalListQuery(
  raw: Record<string, string | undefined>,
): ProfessionalListQueryParseResult {
  const parsed = RawSchema.safeParse(raw)
  if (!parsed.success) return { success: false, error: parsed.error }
  const r = parsed.data

  const filters: ProfessionalListFilters = {}
  if (r.situation.length) filters.situation = r.situation
  if (r.bond.length) filters.bond = r.bond
  if (r.approved) filters.approved = true
  if (r.state.length) filters.state = r.state
  if (r.workModel.length) filters.workModel = r.workModel
  if (r.relocate !== undefined) filters.relocate = r.relocate === 'true'
  if (r.contract.length) filters.contract = r.contract
  if (r.noticeMaxDays !== undefined) filters.noticeMaxDays = r.noticeMaxDays
  if (r.area.length) filters.area = r.area
  if (r.role.length) filters.role = r.role
  if (r.skill.length) {
    filters.skills = r.skill.map((x) => {
      const [skillId, nivel] = x.split(':') as [string, string]
      return { skillId, minLevel: Number(nivel) }
    })
    filters.skillMode = r.skillMode ?? 'all'
  }
  if (r.seniority.length) filters.seniority = r.seniority
  if (r.evidenced) filters.evidenced = true

  const sort: ProfessionalListSort = r.sort
    ? {
        key: r.sort.replace(/^-/, '') as ProfessionalListSortKey,
        dir: r.sort.startsWith('-') ? 'desc' : 'asc',
      }
    : PROFESSIONAL_LIST_DEFAULT_SORT

  return {
    success: true,
    data: {
      page: Math.max(1, lenientInt(r.page, 1)),
      pageSize: Math.min(
        PROFESSIONAL_LIST_PAGE_SIZE_MAX,
        Math.max(1, lenientInt(r.pageSize, PROFESSIONAL_LIST_PAGE_SIZE_DEFAULT)),
      ),
      archived: r.archived === 'true',
      q: r.q ?? '',
      sort,
      columns: r.columns,
      filters,
      ...(r.group ? { group: r.group } : {}),
    },
  }
}

/**
 * O caminho inverso: a consulta como parâmetros de URL, SEM os defaults — a lista
 * comum fica com o URL limpo, e um link só carrega o que alguém mudou.
 */
export function professionalListQueryToParams(
  query: Partial<ProfessionalListQuery>,
): Record<string, string> {
  const out: Record<string, string> = {}
  if (query.page !== undefined && query.page !== 1) out.page = String(query.page)
  if (query.pageSize !== undefined && query.pageSize !== PROFESSIONAL_LIST_PAGE_SIZE_DEFAULT) {
    out.pageSize = String(query.pageSize)
  }
  if (query.archived) out.archived = 'true'
  if (query.q?.trim()) out.q = query.q.trim()
  const sort = query.sort
  if (
    sort &&
    (sort.key !== PROFESSIONAL_LIST_DEFAULT_SORT.key ||
      sort.dir !== PROFESSIONAL_LIST_DEFAULT_SORT.dir)
  ) {
    out.sort = `${sort.dir === 'desc' ? '-' : ''}${sort.key}`
  }
  if (query.group) out.group = query.group
  if (query.columns?.length) out.columns = query.columns.join(',')
  const f = query.filters ?? {}
  if (f.situation?.length) out.situation = f.situation.join(',')
  if (f.bond?.length) out.bond = f.bond.join(',')
  if (f.approved) out.approved = 'true'
  if (f.state?.length) out.state = f.state.join(',')
  if (f.workModel?.length) out.workModel = f.workModel.join(',')
  if (f.relocate !== undefined) out.relocate = String(f.relocate)
  if (f.contract?.length) out.contract = f.contract.join(',')
  if (f.noticeMaxDays !== undefined) out.noticeMaxDays = String(f.noticeMaxDays)
  if (f.area?.length) out.area = f.area.join(',')
  if (f.role?.length) out.role = f.role.join(',')
  if (f.skills?.length) {
    out.skill = f.skills.map((s) => `${s.skillId}:${s.minLevel}`).join(',')
    // «Tem todas» é o default e não se escreve: o URL comum fica limpo. Com UMA skill
    // os dois modos são o mesmo, e escrevê-lo faria uma vista dizer «Modificada» por
    // um valor que nada na tela mostra.
    if (f.skillMode === 'any' && f.skills.length >= 2) out.skillMode = 'any'
  }
  if (f.seniority?.length) out.seniority = f.seniority.join(',')
  if (f.evidenced) out.evidenced = 'true'
  return out
}
